import {Button, DatePicker, message, Modal, Space} from "antd";
import {Controller, useFieldArray, useForm} from "react-hook-form";
import {
  IUnAvailabilityFormValues,
  IUnavailabilitySelection,
} from "./i-unavailability-form";
import {RangeValue} from "rc-picker/lib/interface";
import type {Moment} from "moment";
import {DeleteOutlined, PlusCircleOutlined} from "@ant-design/icons";
import {StyledServiceButtonWrapper} from "../../../../styles/onboarding/onboarding";
import moment from "moment";
import {StyledUnAvailabiltyInputWrapper} from "../availability.style";
import {
  TUnavailabilityRangePayload,
  useUpdateUnavailability,
} from "../../../../react-query-hooks/availability";
import {TUnavailabilityApiResponse} from "../../../../redux/unavailability/i-unavailability-form";

const {RangePicker} = DatePicker;

const DATE_FORMAT = "YYYY-MM-DD";

/**
 * Seeds form rows from the stored set, carrying each row's server id — and the
 * exact `from`/`to` strings it was stored with — into form state so both can be
 * handed straight back on the next save.
 */
const toFormSelections = (
  unavailabilities: TUnavailabilityApiResponse[]
): IUnavailabilitySelection[] =>
  unavailabilities.map((entry) => ({
    unavailabilityId: entry.id,
    storedFrom: entry.from,
    storedTo: entry.to,
    range: [moment(entry.from), moment(entry.to)],
  }));

/**
 * Turns one form row into a payload range.
 *
 * An existing row keeps its `unavailabilityId` so the server updates it in
 * place instead of deleting and re-inserting it. A row whose dates the coach
 * never touched is sent back with its stored strings verbatim: the server
 * compares them as strings to decide whether a range moved, and only ranges
 * that moved (or are new) are held to the "not in the past" rule.
 */
const toPayloadRange = (
  selection: IUnavailabilitySelection
): TUnavailabilityRangePayload | null => {
  const [from, to] = selection.range ?? [null, null];

  if (!from || !to) {
    return null;
  }

  const {unavailabilityId, storedFrom, storedTo} = selection;

  const isUnchanged =
    unavailabilityId !== undefined &&
    storedFrom !== undefined &&
    storedTo !== undefined &&
    moment(storedFrom).isSame(from, "day") &&
    moment(storedTo).isSame(to, "day");

  return {
    ...(unavailabilityId !== undefined && {unavailabilityId}),
    from: isUnchanged ? (storedFrom as string) : from.format(DATE_FORMAT),
    to: isUnchanged ? (storedTo as string) : to.format(DATE_FORMAT),
  };
};

interface IUnAvailabilityFormProps {
  unavailabilities: TUnavailabilityApiResponse[];
}

const UnavailabilityForm: React.FC<IUnAvailabilityFormProps> = ({unavailabilities}) => {
  const updateUnavailabilityQueryMutation = useUpdateUnavailability();

  const {
    handleSubmit,
    formState: {errors},
    reset,
    control,
  } = useForm<IUnAvailabilityFormValues>({
    mode: "onChange",
    defaultValues: {
      selections: toFormSelections(unavailabilities),
    },
  });
  const {fields, append, remove} = useFieldArray({
    control,
    name: "selections",
  });

  const handleAppend = () => {
    append({range: null});
  };

  const saveSelections = (selections: TUnavailabilityRangePayload[]) => {
    updateUnavailabilityQueryMutation.mutate(
      {selections},
      {
        onSuccess: (response) => {
          // Re-seed from the saved set so rows inserted by this save pick up
          // their new ids straight away — a follow-up save would otherwise
          // resend them id-less and churn them.
          reset({
            selections: toFormSelections(
              Array.isArray(response?.data) ? response.data : []
            ),
          });
          window.scrollTo(0, 0);
          message.success("Your unavailable dates has been saved successfully!");
        },
      }
    );
  };

  const onSubmit = async (values: IUnAvailabilityFormValues) => {
    const rows = values.selections ?? [];

    // Saving replaces the whole set, so a half-filled row would be silently
    // dropped rather than saved. Make the coach resolve it instead.
    if (rows.some((row) => !row.range?.[0] || !row.range?.[1])) {
      message.error(
        "Please pick a start and an end date for every range, or remove the empty ones."
      );
      return;
    }

    // The server rejects duplicates too, but it compares the submitted strings
    // as-is — so an untouched row and a new row covering the same days can slip
    // past it whenever the stored string is formatted differently. Compare the
    // days the coach actually picked.
    const days = rows.map(
      (row) =>
        `${row.range?.[0]?.format(DATE_FORMAT)}|${row.range?.[1]?.format(DATE_FORMAT)}`
    );
    if (new Set(days).size !== days.length) {
      message.error("Duplicate date ranges detected. Please remove the repeated ones.");
      return;
    }

    const selections = rows
      .map(toPayloadRange)
      .filter((range): range is TUnavailabilityRangePayload => range !== null);

    if (selections.length === 0 && unavailabilities.length > 0) {
      Modal.confirm({
        title: "Remove all unavailable dates?",
        content:
          "Saving with no date ranges clears every blocked date from your booking calendar.",
        okText: "Yes, clear them",
        okButtonProps: {danger: true},
        cancelText: "Cancel",
        onOk: () => saveSelections(selections),
      });
      return;
    }

    saveSelections(selections);
  };

  return (
    <Space direction="vertical" size={24} style={{display: "flex"}}>
      <form style={{marginTop: "2.4rem"}} onSubmit={handleSubmit(onSubmit)}>
        {fields.map((item, index) => (
          <StyledUnAvailabiltyInputWrapper key={item.id}>
            <Controller
              control={control}
              name={`selections.${index}.range` as "selections.0.range"}
              render={({field: {onChange, value}}) => (
                <RangePicker
                  size="large"
                  onChange={(values: RangeValue<Moment>, _) => onChange(values)}
                  value={value as RangeValue<Moment>}
                />
              )}
            />
            {/* Removal is local only: it takes effect when the coach saves,
                and "Discard" puts the row back. */}
            <Button
              onClick={() => remove(index)}
              shape="circle"
              icon={<DeleteOutlined />}
              size="small"
              style={{border: 0, marginLeft: "1.6rem"}}
              aria-label="Remove date range"
            />
          </StyledUnAvailabiltyInputWrapper>
        ))}
        <div>
          <Button
            onClick={handleAppend}
            size="small"
            type="link"
            icon={<PlusCircleOutlined />}
            style={{paddingLeft: 0}}
          >
            Add date range
          </Button>
        </div>
        <StyledServiceButtonWrapper style={{marginTop: "2.4rem"}}>
          <div>
            <Button
              disabled={Object.keys(errors).length > 0}
              loading={updateUnavailabilityQueryMutation.isPending}
              block
              type="primary"
              htmlType="submit"
              size="small"
            >
              Save changes
            </Button>
          </div>
          <div>
            <Button onClick={() => reset()} block type="text" danger size="small">
              Discard
            </Button>
          </div>
        </StyledServiceButtonWrapper>
      </form>
    </Space>
  );
};
export {UnavailabilityForm};
